import React, { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import api, { apiErrorText } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { fmtDate } from "../lib/format";
import {
  Plus,
  Trash2,
  Pencil,
  ChevronLeft,
  ChevronRight,
  Settings2,
  X,
  Send,
} from "lucide-react";
import { toast } from "sonner";

const fmtUSDT = (n) =>
  Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 4 });

const monthAdd = (ym, n) => {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1 + n, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
};
const currentMonth = () => {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
};
const monthLabel = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
};
const todayISO = () => new Date().toISOString().slice(0, 10);

const EMPTY_FORM = {
  date: todayISO(),
  amount_usdt: "",
  category: "",
  recipient: "",
  description: "",
  notes: "",
};

export default function USDTSent() {
  const [month, setMonth] = useState(currentMonth());
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [categories, setCategories] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [recipientFilter, setRecipientFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const [catOpen, setCatOpen] = useState(false);
  const [catDraft, setCatDraft] = useState([]);
  const [catInput, setCatInput] = useState("");

  const monthRange = useMemo(() => {
    const [y, m] = month.split("-").map(Number);
    const start = `${month}-01`;
    const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
    const end = `${month}-${String(last).padStart(2, "0")}`;
    return { from: start, to: end };
  }, [month]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { from: monthRange.from, to: monthRange.to };
      if (categoryFilter !== "all") params.category = categoryFilter;
      if (recipientFilter.trim()) params.recipient = recipientFilter.trim();
      const [list, sum, cats] = await Promise.all([
        api.get("/usdt-sent", { params }),
        api.get("/usdt-sent/summary", { params: { month } }),
        api.get("/usdt-sent/categories"),
      ]);
      setItems(list.data);
      setSummary(sum.data);
      setCategories(cats.data.categories || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load(); // eslint-disable-next-line
  }, [month, categoryFilter, recipientFilter]);

  const openCreate = () => {
    setEditing(null);
    setForm({
      ...EMPTY_FORM,
      date: monthRange.to >= todayISO() ? todayISO() : monthRange.to,
    });
    setOpen(true);
  };
  const openEdit = (e) => {
    setEditing(e);
    setForm({
      date: e.date,
      amount_usdt: String(e.amount_usdt || ""),
      category: e.category || "",
      recipient: e.recipient || "",
      description: e.description || "",
      notes: e.notes || "",
    });
    setOpen(true);
  };

  const save = async () => {
    const u = Number(form.amount_usdt);
    if (!u || u <= 0) return toast.error("Enter a valid USDT amount");
    const payload = {
      date: form.date,
      amount_usdt: u,
      category: form.category || null,
      recipient: form.recipient || null,
      description: form.description || null,
      notes: form.notes || null,
    };
    try {
      if (editing) {
        await api.put(`/usdt-sent/${editing.id}`, payload);
        toast.success("Entry updated");
      } else {
        await api.post("/usdt-sent", payload);
        toast.success("Entry added");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiErrorText(e));
    }
  };

  const remove = async (e) => {
    if (!window.confirm(`Delete entry of ${fmtUSDT(e.amount_usdt)} USDT?`))
      return;
    try {
      await api.delete(`/usdt-sent/${e.id}`);
      toast.success("Deleted");
      load();
    } catch (er) {
      toast.error(apiErrorText(er));
    }
  };

  const openCats = () => {
    setCatDraft([...categories]);
    setCatInput("");
    setCatOpen(true);
  };
  const saveCats = async () => {
    try {
      await api.put("/usdt-sent/categories", { categories: catDraft });
      toast.success("Categories updated");
      setCatOpen(false);
      load();
    } catch (e) {
      toast.error(apiErrorText(e));
    }
  };

  const sortedCategories = Object.entries(summary?.by_category || {}).sort(
    (a, b) => (b[1].amount_usdt || 0) - (a[1].amount_usdt || 0)
  );
  const sortedRecipients = Object.entries(summary?.by_recipient || {}).sort(
    (a, b) => (b[1].amount_usdt || 0) - (a[1].amount_usdt || 0)
  );

  return (
    <Layout
      title="USDT Sent"
      subtitle="Track outbound USDT with custom categories · monthly view + filters"
      testId="usdt-sent-page"
      actions={
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={openCats}
            className="h-9"
            data-testid="usdt-sent-manage-categories-btn"
          >
            <Settings2 size={14} className="mr-1.5" /> Manage categories
          </Button>
          <Button
            onClick={openCreate}
            className="h-9 bg-zinc-900 hover:bg-zinc-800 text-white"
            data-testid="add-usdt-sent-btn"
          >
            <Plus size={14} className="mr-1.5" /> Add entry
          </Button>
        </div>
      }
    >
      {/* Month nav + filters */}
      <div className="stat-card p-3 mb-5 flex flex-wrap items-center gap-2">
        <button
          onClick={() => setMonth(monthAdd(month, -1))}
          className="h-8 w-8 rounded-md border hairline bg-white hover:bg-zinc-50 flex items-center justify-center"
          data-testid="usdt-sent-prev-month"
        >
          <ChevronLeft size={14} strokeWidth={1.7} />
        </button>
        <div
          className="font-display text-[15px] font-semibold tracking-tight px-2"
          data-testid="usdt-sent-current-month"
        >
          {monthLabel(month)}
        </div>
        <button
          onClick={() => setMonth(monthAdd(month, 1))}
          className="h-8 w-8 rounded-md border hairline bg-white hover:bg-zinc-50 flex items-center justify-center"
          data-testid="usdt-sent-next-month"
        >
          <ChevronRight size={14} strokeWidth={1.7} />
        </button>
        {month !== currentMonth() && (
          <button
            onClick={() => setMonth(currentMonth())}
            className="h-8 px-3 rounded-md text-[12.5px] border hairline bg-white hover:bg-zinc-50 ml-1"
            data-testid="usdt-sent-this-month"
          >
            This month
          </button>
        )}
        <div className="flex items-center gap-2 ml-auto">
          <Input
            value={recipientFilter}
            onChange={(e) => setRecipientFilter(e.target.value)}
            placeholder="Recipient…"
            className="h-8 w-[180px]"
            data-testid="usdt-sent-filter-recipient"
          />
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger
              className="h-8 w-[180px]"
              data-testid="usdt-sent-filter-category"
            >
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
        <div className="stat-card p-4" data-testid="usdt-sent-total">
          <div className="text-[10.5px] uppercase tracking-widest text-zinc-500">
            Total · USDT
          </div>
          <div className="font-mono text-[22px] font-medium mt-1 text-amber-700">
            {fmtUSDT(summary?.total_usdt)}
          </div>
          <div className="text-[10.5px] text-zinc-500 mt-0.5">
            {monthLabel(month)}
          </div>
        </div>
        <div className="stat-card p-4" data-testid="usdt-sent-count">
          <div className="text-[10.5px] uppercase tracking-widest text-zinc-500">
            Entries
          </div>
          <div className="font-mono text-[22px] font-medium mt-1">
            {summary?.count || 0}
          </div>
          <div className="text-[10.5px] text-zinc-500 mt-0.5">this month</div>
        </div>
        <div className="stat-card p-4" data-testid="usdt-sent-top-category">
          <div className="text-[10.5px] uppercase tracking-widest text-zinc-500">
            Top category
          </div>
          {sortedCategories.length === 0 ? (
            <div className="font-mono text-[15px] font-medium mt-1 text-zinc-400">
              —
            </div>
          ) : (
            <>
              <div className="font-display text-[16px] font-semibold mt-1 truncate">
                {sortedCategories[0][0]}
              </div>
              <div className="text-[10.5px] text-zinc-500 mt-0.5 font-mono">
                {sortedCategories[0][1].count} entries ·{" "}
                {fmtUSDT(sortedCategories[0][1].amount_usdt)} USDT
              </div>
            </>
          )}
        </div>
      </div>

      {/* Category & Recipient breakdown */}
      {summary?.count > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-5">
          <div className="stat-card p-4">
            <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-3">
              By category
            </div>
            <div className="space-y-1.5">
              {sortedCategories.map(([name, info]) => (
                <div
                  key={name}
                  className="flex items-center justify-between text-[12.5px] border-b hairline pb-1.5 last:border-0"
                  data-testid={`usdt-sent-cat-${name}`}
                >
                  <span className="font-medium">{name}</span>
                  <span className="font-mono text-amber-700">
                    {fmtUSDT(info.amount_usdt)} USDT
                    <span className="text-[10.5px] text-zinc-400 ml-1.5">
                      · {info.count}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div className="stat-card p-4">
            <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-3">
              By recipient
            </div>
            <div className="space-y-1.5">
              {sortedRecipients.slice(0, 8).map(([name, info]) => (
                <div
                  key={name}
                  className="flex items-center justify-between text-[12.5px] border-b hairline pb-1.5 last:border-0"
                >
                  <span className="font-medium truncate">{name}</span>
                  <span className="font-mono text-amber-700">
                    {fmtUSDT(info.amount_usdt)} USDT
                    <span className="text-[10.5px] text-zinc-400 ml-1.5">
                      · {info.count}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="stat-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-[13px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-widest text-zinc-500 border-b hairline bg-zinc-50/50">
                <th className="py-3 px-4 font-medium">Date</th>
                <th className="py-3 px-4 font-medium">Category</th>
                <th className="py-3 px-4 font-medium">Recipient</th>
                <th className="py-3 px-4 font-medium">Description</th>
                <th className="py-3 px-4 font-medium text-right">USDT</th>
                <th className="py-3 px-4 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y hairline">
              {loading ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-zinc-500">
                    Loading…
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-zinc-500">
                    <Send
                      size={20}
                      strokeWidth={1.5}
                      className="mx-auto mb-2 text-zinc-400"
                    />
                    No entries for {monthLabel(month)}.
                  </td>
                </tr>
              ) : (
                items.map((e) => (
                  <tr
                    key={e.id}
                    className="row-hover"
                    data-testid={`usdt-sent-row-${e.id}`}
                  >
                    <td className="py-3 px-4 font-mono text-[12.5px]">
                      {fmtDate(e.date)}
                    </td>
                    <td className="py-3 px-4 text-[12.5px]">
                      {e.category ? (
                        <span className="inline-flex text-[11px] px-1.5 py-0.5 rounded border bg-amber-50 border-amber-200 text-amber-800">
                          {e.category}
                        </span>
                      ) : (
                        <span className="text-zinc-400">Uncategorized</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-[12.5px] text-zinc-700">
                      {e.recipient || "—"}
                    </td>
                    <td className="py-3 px-4 max-w-[260px]">
                      <div className="truncate font-medium">
                        {e.description || (
                          <span className="text-zinc-400">—</span>
                        )}
                      </div>
                      {e.notes && (
                        <div className="text-[11.5px] text-zinc-500 truncate">
                          {e.notes}
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-4 font-mono text-right text-amber-700">
                      {fmtUSDT(e.amount_usdt)} USDT
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEdit(e)}
                          className="p-1.5 rounded hover:bg-zinc-100 text-zinc-600"
                          data-testid={`edit-usdt-sent-${e.id}`}
                        >
                          <Pencil size={14} strokeWidth={1.6} />
                        </button>
                        <button
                          onClick={() => remove(e)}
                          className="p-1.5 rounded hover:bg-rose-50 text-rose-600"
                          data-testid={`delete-usdt-sent-${e.id}`}
                        >
                          <Trash2 size={14} strokeWidth={1.6} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="usdt-sent-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editing ? "Edit entry" : "Add USDT sent"}
            </DialogTitle>
            <DialogDescription>
              Record an outbound USDT transfer with optional category &
              recipient.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Date *</Label>
              <Input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                data-testid="usdt-sent-date-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Amount (USDT) *</Label>
              <Input
                type="number"
                step="0.0001"
                value={form.amount_usdt}
                onChange={(e) =>
                  setForm({ ...form, amount_usdt: e.target.value })
                }
                placeholder="0.00"
                data-testid="usdt-sent-amount-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Select
                value={form.category || "__none"}
                onValueChange={(v) =>
                  setForm({ ...form, category: v === "__none" ? "" : v })
                }
              >
                <SelectTrigger data-testid="usdt-sent-category-select">
                  <SelectValue placeholder="Optional" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">— None —</SelectItem>
                  {categories.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Recipient</Label>
              <Input
                value={form.recipient}
                onChange={(e) =>
                  setForm({ ...form, recipient: e.target.value })
                }
                placeholder="Optional"
                data-testid="usdt-sent-recipient-input"
              />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Description</Label>
              <Input
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
                placeholder="e.g. Q1 commission to Alice"
                data-testid="usdt-sent-description-input"
              />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Notes</Label>
              <Input
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Optional"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={save}
              className="bg-zinc-900 hover:bg-zinc-800 text-white"
              data-testid="save-usdt-sent-btn"
            >
              {editing ? "Save changes" : "Add entry"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage categories */}
      <Dialog open={catOpen} onOpenChange={setCatOpen}>
        <DialogContent data-testid="usdt-sent-categories-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              Manage categories
            </DialogTitle>
            <DialogDescription>
              Custom categories for grouping USDT sent entries.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-1.5">
            <Input
              value={catInput}
              onChange={(e) => setCatInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  const v = catInput.trim();
                  if (!v) return;
                  if (catDraft.some((x) => x.toLowerCase() === v.toLowerCase()))
                    return toast.message(`"${v}" already exists`);
                  setCatDraft([...catDraft, v]);
                  setCatInput("");
                }
              }}
              placeholder="e.g. Commission"
              className="h-9"
              data-testid="usdt-sent-category-input"
            />
            <Button
              onClick={() => {
                const v = catInput.trim();
                if (!v) return;
                if (catDraft.some((x) => x.toLowerCase() === v.toLowerCase()))
                  return toast.message(`"${v}" already exists`);
                setCatDraft([...catDraft, v]);
                setCatInput("");
              }}
              className="h-9 bg-zinc-900 hover:bg-zinc-800 text-white"
            >
              <Plus size={13} />
            </Button>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {catDraft.map((c, i) => (
              <span
                key={`${c}-${i}`}
                className="inline-flex items-center gap-1 text-[12px] border hairline rounded px-1.5 py-0.5 bg-zinc-50"
              >
                {c}
                <button
                  onClick={() =>
                    setCatDraft(catDraft.filter((_, idx) => idx !== i))
                  }
                  className="text-zinc-400 hover:text-rose-600"
                >
                  <X size={11} />
                </button>
              </span>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCatOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={saveCats}
              className="bg-zinc-900 hover:bg-zinc-800 text-white"
              data-testid="save-usdt-sent-categories-btn"
            >
              Save categories
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
